import { useCallback, useEffect, useState } from 'react';

import Button from '@/common/Button';
import Modal from '@/common/Modal';
import Textarea from '@/common/Textarea';
import Toast from '@/models/Toast';
import Logger from '@/utils/logs';
import { createNotification } from '@/utils';

const log = Logger.of('ReportProblem');
const title = 'Report a Problem';
export const MAX_MESSAGE_LENGTH = 1000;

export type ReportProblemModalProps = {
  show: boolean;
  screenshot?: string;
  onHide: () => void;
  /** resolves the server response; failures are already toasted by the global error link */
  onSubmit: (message: string) => Promise<{ message?: string; error?: unknown } | undefined>;
};

const ReportProblemModal = ({ show, screenshot, onHide, onSubmit }: ReportProblemModalProps): JSX.Element => {
  const [message, setMessage] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect((): void => {
    if (show) setMessage('');
  }, [show]);

  // the page's Filters hotkeys (shift+enter, double enter) listen on body/window and would swallow new lines;
  // stop Enter at the textarea so it only adds a line break (Escape still reaches the modal)
  const keepEnterInTextarea = useCallback((node: HTMLTextAreaElement | null): void => {
    node?.addEventListener('keydown', (event: KeyboardEvent): void => {
      if (event.key === 'Enter') event.stopPropagation();
    });
  }, []);

  const handleHide = (): void => {
    if (submitting) return;
    onHide();
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmed = message.trim();
    if (!trimmed.length || submitting) return;
    setSubmitting(true);
    try {
      const res = await onSubmit(trimmed);
      // keep the modal open to retry; the error was already shown by the global error link
      if (!res || res.error) return;
      createNotification(res.message || 'Thanks! Your report was sent to support.', Toast.Type.SUCCESS, title);
      onHide();
    } catch (err) {
      log.warn('failed to send report', err?.message || err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      title={title.toUpperCase()}
      centered
      footer={(): JSX.Element => (
        <>
          <Button name="CANCEL" variant="secondary" onClick={handleHide} disabled={submitting}>
            Cancel
          </Button>
          <Button name="SUBMIT" variant="primary" onClick={handleSubmit} disabled={!message.trim().length || submitting}>
            {submitting ? 'Sending...' : 'Submit'}
          </Button>
        </>
      )}
    >
      <div className="ReportProblemModal d-flex flex-column gap-3">
        <Textarea
          ref={keepEnterInTextarea}
          name="message"
          placeholder="Describe the problem you're seeing"
          value={message}
          onChange={(value): void => setMessage(String(value || ''))}
          maxLength={MAX_MESSAGE_LENGTH}
          rows={5}
          autoFocus
        />
        {screenshot && (
          <div className="d-flex flex-column gap-1">
            <small className="text-secondary">A screenshot of this page will be attached.</small>
            <img
              src={screenshot}
              alt="Screenshot to be attached"
              style={{ maxHeight: 160, objectFit: 'contain', border: '1px solid #ddd' }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReportProblemModal;
